import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { first } from 'lodash';

import { useTranslation } from 'providers/GILT.provider';
import { selectCashup, selectCashupSelectedStylistIds, selectCashupSoldVouchers } from 'selectors/cashup.selectors';
import { selectCheckoutTxLog } from 'selectors/checkout.selectors';
import { selectCurrentSalon, selectCurrentSalonClients, selectCurrentSalonId } from 'selectors/currentSalon.selectors';
import { selectRsAbeCashupAllCombinedRead, selectRsAbeCashupOwnCombinedRead, selectRsBeGetCashup, selectRsBeGetReconcile, selectRsBeListReconcile } from 'selectors/request.selectors';
import asArray from 'utils/arrays/asArray.util';
import asObject from 'utils/objects/asObject.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import { getTransactionsByStylist } from 'cashup/actions';
import CashupLogs from 'cashup/CashupLogs';
import { clearTransactionLog, exportTransactionLog } from 'checkout/actions';

const CashupLoader = () => {
  const { t } = useTranslation();

  return (
    <div data-bem="CashupLoader" className="loading-wrap" style={{ position: 'fixed' }}>
      <div data-bem="CashupLoader__animation" className="loading-anim">
        <div data-bem="CashupLoader__title" className="loading-title">
          <p data-bem="CashupLoader__label">{t('cashUp.cashupLoader')}</p>
        </div>
      </div>
      <div data-bem="CashupLoader__feedback" className="loading-feedback">
        {t('cashUp.cashupLoaderMessage')}
      </div>
    </div>
  );
};

export const CashupLogsWrapper = ({
  // from parent
  originals,
  onChangeOrder,
  onChangePage,
  onCheckout,
  onViewReceipt,
  onVoidTransaction,
  byRangeModal,
  selectedFromDate,
  selectedToDate,
  selectedStaffGroup,
  currentPage,
}) => {
  const dispatch = useDispatch();

  const cashupFromProps = useSelector(selectCashup);
  const clientsFromProps = useSelector(selectCurrentSalonClients);
  const rsBeGetCashupRaw = useSelector(selectRsBeGetCashup);
  const rsAbeCashupAllCombinedRead = useSelector(selectRsAbeCashupAllCombinedRead);
  const rsAbeCashupOwnCombinedRead = useSelector(selectRsAbeCashupOwnCombinedRead);
  const rsBeGetCashup = rsBeGetCashupRaw || rsAbeCashupAllCombinedRead || rsAbeCashupOwnCombinedRead;
  const rsBeGetReconcile = useSelector(selectRsBeGetReconcile);
  const rsBeListReconcile = useSelector(selectRsBeListReconcile);
  const salonId = useSelector(selectCurrentSalonId);
  const salonDetails = useSelector(selectCurrentSalon);
  const selectedStylistIds = useSelector(selectCashupSelectedStylistIds);
  const transactionLog = useSelector(selectCheckoutTxLog);
  const voucherSoldList = useSelector(selectCashupSoldVouchers);

  const dispatchClearTransactionLog = useCallback((...args) => dispatch(clearTransactionLog(...args)), [dispatch]);
  const dispatchExportTransactionLog = useCallback((...args) => dispatch(exportTransactionLog(...args)), [dispatch]);
  const dispatchGetTransactionsByStylist = useCallback((...args) => dispatch(getTransactionsByStylist(...args)), [dispatch]);

  const { apiData, apiTransactionData, depositApiData, reconcileList } = asObject(cashupFromProps);
  const { isFetching: depositDataIsFetching, depositsLogs } = asObject(depositApiData);
  const { isFetching: apiDataIsFetching, count } = asObject(apiData);

  if (apiDataIsFetching || !isFullArray(clientsFromProps) || depositDataIsFetching || rsBeGetCashup || rsBeGetReconcile || rsBeListReconcile) {
    // NOTE: !(isFullArray(apiData.results) || isFullArray(depositsLogs)) were making the logs table hidden
    return <CashupLoader />;
  }

  const pageCount = Math.ceil(count / 100);

  const timeZone = salonDetails.timezone;

  const logs = asArray(depositsLogs);
  const clients = asArray(clientsFromProps);

  const depositData = {
    ...asObject(depositApiData),
    depositsLogs: logs.map((deposit) => ({
      ...first(clients.filter((client) => client && client.id === asObject(deposit).clientId)),
      ...deposit,
    })),
  };

  return (
    <CashupLogs
      cashUpTotals={apiData}
      depositData={depositData}
      transactions={originals}
      pageCount={pageCount}
      changePage={onChangePage}
      currentPage={currentPage}
      changeOrder={onChangeOrder}
      rsBeGetCashup={rsBeGetCashup}
      onViewReceipt={onViewReceipt}
      onVoidTransaction={onVoidTransaction}
      onCheckout={onCheckout}
      reconcileList={reconcileList}
      voucherSoldList={voucherSoldList}
      transactionLog={transactionLog}
      transactionLogSingleStylist={apiTransactionData}
      exportTransactionLog={dispatchExportTransactionLog}
      getTransactionsByStylist={dispatchGetTransactionsByStylist}
      salonId={salonId}
      startDate={selectedFromDate}
      endDate={selectedToDate}
      timeZone={timeZone}
      byRangeModal={byRangeModal}
      selectedStaffGroup={selectedStaffGroup}
      selectedStylistIds={selectedStylistIds}
      clearTransactionLog={dispatchClearTransactionLog}
    />
  );
};
